import { Fragment } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useMatch,
  useNavigate,
} from "react-router-dom";
import { LogOut } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../slices/authSlice";
import { toast } from "react-toastify";
import AppSwitcher from "../components/AppSwitcher";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "../components/ui/sidebar";
import { exchangeGroups } from "../config/exchanges";
import { cn } from "../lib/utils";

export function AdminShell() {
  return (
    <SidebarProvider defaultOpen={false}>
      <AppSidebar />
      <SidebarInset className="bg-transparent" data-web3-section>
        <TopBar />
        <main className="relative flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[1440px] px-5 py-6 sm:px-8 sm:py-8 md:px-10 md:py-10">
            <Outlet />
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}

function AppSidebar() {
  return (
    <Sidebar
      collapsible="offcanvas"
      className="border-r border-sidebar-border bg-[#0a0c10]"
      data-web3-section
    >
      <SidebarHeader className="h-16 flex-row items-center gap-3 px-5">
        <img
          src="/logo.png"
          alt="Ginox"
          className="h-9 w-9 shrink-0 object-contain"
        />
        <p className="font-serif text-[22px] italic leading-none text-sidebar-foreground">
          Gidex
        </p>
      </SidebarHeader>

      <div
        className="mx-5 h-[1px] bg-sidebar-border"
        style={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}
      />

      <SidebarContent className="px-2 py-4">
        <SidebarGroup>
          <SidebarGroupLabel className="px-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/60">
            Web3 Exchanges
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {exchangeGroups.map((group) => (
                <ExchangeItem key={group.id} group={group} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}

function ExchangeItem({ group }) {
  const Icon = group.icon;
  const enabledItems = group.items.filter((i) => !i.disabled);

  if (enabledItems.length === 0) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          disabled
          aria-disabled="true"
          className="cursor-not-allowed text-muted-foreground/40 hover:bg-transparent hover:text-muted-foreground/40"
        >
          <Icon className="h-4 w-4" />
          <span>{group.label}</span>
          <span className="ml-auto rounded-full border border-border/60 px-1.5 py-px text-[8px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/60">
            Soon
          </span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  // Single page → one row linking straight to it.
  if (enabledItems.length === 1) {
    return <ExchangeLink to={enabledItems[0].to} icon={Icon} label={group.label} />;
  }

  // Multiple pages → group header + indented sub-links.
  return (
    <>
      <SidebarMenuItem>
        <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground">
          <Icon className="h-4 w-4" />
          <span>{group.label}</span>
        </div>
      </SidebarMenuItem>
      {enabledItems.map((item) => (
        <ExchangeLink key={item.to} to={item.to} label={item.label} sub />
      ))}
    </>
  );
}

function ExchangeLink({ to, icon: Icon, label, sub }) {
  const match = useMatch({ path: to ?? "__never__", end: false });
  const isActive = Boolean(to && match);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={isActive}
        className={cn(
          "relative",
          sub && "pl-9 text-[13px]",
          isActive &&
            "before:absolute  bg-[#1a1d23]  before:left-0 before:top-1/2 before:h-5 before:w-px before:-translate-y-1/2 before:bg-ginox-green",
        )}
      >
        <NavLink to={to}>
          {Icon ? <Icon className="h-4 w-4" /> : null}
          <span>{label}</span>
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function TopBar() {
  const location = useLocation();
  const crumbs = buildCrumbs(location.pathname);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const handleLogout = () => {
    dispatch(logoutUser());
    toast.success("Logged out successfully");
    navigate("/web3/login");
  };

  return (
    <header
      className="flex h-14 items-center gap-2 border-b border-border bg-topbar px-3 sm:h-16 sm:gap-3 sm:px-4"
      style={{ backgroundColor: "#0b0e11" }}
    >
      <SidebarTrigger className="h-9 w-9 rounded-md border border-border/70 bg-white/[0.02] text-muted-foreground hover:text-foreground [&_svg]:h-4 [&_svg]:w-4" />
      <nav
        aria-label="Breadcrumb"
        className="flex min-w-0 flex-1 items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground"
      >
        {crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1;
          const hideOnMobile = !isLast;
          return (
            <Fragment key={`${crumb.label}-${i}`}>
              {i > 0 && (
                <span
                  className={cn(
                    "text-muted-foreground/30",
                    hideOnMobile && "hidden sm:inline",
                  )}
                >
                  /
                </span>
              )}
              {crumb.to ? (
                <Link
                  to={crumb.to}
                  className={cn(
                    "truncate transition-colors hover:text-foreground",
                    hideOnMobile && "hidden sm:inline",
                  )}
                >
                  {crumb.label}
                </Link>
              ) : (
                <span
                  className={cn(
                    "truncate",
                    isLast && "text-foreground",
                    hideOnMobile && "hidden sm:inline",
                  )}
                  aria-current={isLast ? "page" : undefined}
                >
                  {crumb.label}
                </span>
              )}
            </Fragment>
          );
        })}
      </nav>

      {/* App switcher, user info & logout */}
      <div className="flex items-center gap-3">
        <AppSwitcher inApp />
        {user && (
          <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground">
            <span className="truncate max-w-[150px]">
              {user.email || user.name}
            </span>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 rounded-md border border-border/70 bg-white/[0.02] px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-foreground"
          title="Logout"
        >
          <LogOut className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Logout</span>
        </button>
      </div>
    </header>
  );
}

const HYPERLIQUID_FILLS = "/web3/hyperliquid/fills";
const COMBINED_DASHBOARD = "/web3/combined";

function buildCrumbs(pathname) {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return [{ label: "Home" }];

  const isWeb3 = parts[0] === "web3";
  const isCombined = isWeb3 && parts[1] === "combined";
  const isHyperliquid = isWeb3 && parts[1] === "hyperliquid";
  const isForex = isWeb3 && parts[1] === "forex";
  const isFills = isHyperliquid && parts[2] === "fills";
  const isFees = isHyperliquid && parts[2] === "fees";
  const walletAddress = isFills && parts.length >= 4 ? parts[3] : undefined;
  const onFillsList = isFills && !walletAddress;

  if (!isWeb3) {
    return parts.map((p) => ({
      label: p.length > 24 ? `${p.slice(0, 6)}…${p.slice(-4)}` : titleCase(p),
    }));
  }

  const crumbs = [{ label: "Web3", to: COMBINED_DASHBOARD }];

  if (isCombined) {
    crumbs.push({ label: "Combined Dashboard" });
  }

  if (isHyperliquid) {
    crumbs.push({ label: "Hyperliquid", to: HYPERLIQUID_FILLS });
  }

  if (isFills) {
    crumbs.push({
      label: "Fills",
      to: onFillsList ? undefined : HYPERLIQUID_FILLS,
    });
  }

  if (isFees) {
    crumbs.push({ label: "Fees" });
  }

  if (isForex) {
    crumbs.push({ label: "Forex", to: "/web3/forex/withdrawals" });
  }

  if (walletAddress) {
    crumbs.push({
      label:
        walletAddress.length > 12
          ? `${walletAddress.slice(0, 6)}…${walletAddress.slice(-4)}`
          : walletAddress,
    });
  }

  return crumbs;
}

function titleCase(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
